/**
 * HUD: állapotsáv, célkereszt, üzenetek, sebzés-irányjelzők.
 *
 * DOM-alapú és nem canvas: a szöveg így éles marad a csökkentett belső
 * renderfelbontás mellett is (a játék képe szándékosan pixeles — a HUD-nak
 * viszont olvashatónak kell lennie), és nem terheli a rajzolási hurkot.
 * Frissítés csak változáskor: a `textContent` írása is layoutot okoz.
 *
 * Gazda: C
 */

import type { HudState, KeyColor } from '../core/contracts'

const KEY_COLORS: Record<KeyColor, string> = {
  blue: '#64c4cf',
  yellow: '#e4b02c',
  red: '#ff3b1f',
}

const WEAPON_LABELS: Record<string, string> = {
  fist: 'Ököl',
  pistol: 'Pisztoly',
  shotgun: 'Sörétes',
  chaingun: 'Gépágyú',
  rocket: 'Rakétavető',
  plasma: 'Plazmavető',
}

const AMMO_LABELS: Record<string, string> = {
  bullet: 'Lőszer',
  shell: 'Sörét',
  rocket: 'Rakéta',
  cell: 'Cella',
  none: '—',
}

interface MessageItem {
  el: HTMLElement
  ttl: number
}

export class Hud {
  readonly root: HTMLElement

  private elHealth: HTMLElement
  private elArmor: HTMLElement
  private elArmorWrap: HTMLElement
  private elAmmo: HTMLElement
  private elAmmoType: HTMLElement
  private elWeapon: HTMLElement
  private elSlots: HTMLElement
  private elKeys: HTMLElement
  private elEnemies: HTMLElement
  private elSecrets: HTMLElement
  private elMessages: HTMLElement
  private elCrosshair: HTMLElement
  private elHitMarker: HTMLElement
  private elLowHealth: HTMLElement

  private messages: MessageItem[] = []
  private hitMarkerT = 0
  private regenActive = false
  private slotEls = new Map<number, HTMLElement>()

  // Az utolsó kirajzolt értékek — csak változáskor nyúlunk a DOM-hoz.
  private last = {
    health: -1,
    armor: -1,
    ammo: -1,
    ammoType: '',
    weapon: '',
    enemies: -1,
    secrets: -1,
    keys: '',
    slots: '',
  }

  constructor(parent: HTMLElement) {
    this.root = el('div', 'hud')

    // --- Célkereszt ---
    this.elCrosshair = el('div', 'crosshair')
    this.elCrosshair.innerHTML =
      '<span class="ch-t"></span><span class="ch-r"></span><span class="ch-b"></span><span class="ch-l"></span><span class="ch-c"></span>'
    this.root.appendChild(this.elCrosshair)

    this.elHitMarker = el('div', 'hitmarker')
    this.elHitMarker.innerHTML = '<span></span><span></span><span></span><span></span>'
    this.root.appendChild(this.elHitMarker)

    // --- Üzenetsor ---
    this.elMessages = el('div', 'messages')
    this.root.appendChild(this.elMessages)

    // --- Alsó állapotsáv ---
    const bar = el('div', 'statusbar')

    const left = el('div', 'stat stat-left')
    this.elHealth = el('span', 'stat-value')
    this.elHealth.textContent = '100'
    left.append(labelled('Élet', this.elHealth))

    this.elArmorWrap = el('div', 'stat stat-armor')
    this.elArmor = el('span', 'stat-value')
    this.elArmor.textContent = '0'
    this.elArmorWrap.append(labelled('Páncél', this.elArmor))

    const center = el('div', 'stat stat-center')
    this.elWeapon = el('div', 'weapon-name')
    this.elSlots = el('div', 'slots')
    center.append(this.elWeapon, this.elSlots)

    const right = el('div', 'stat stat-right')
    this.elAmmo = el('span', 'stat-value')
    this.elAmmo.textContent = '0'
    this.elAmmoType = el('span', 'stat-label')
    this.elAmmoType.textContent = 'Lőszer'
    const ammoBlock = el('div', 'stat-block')
    ammoBlock.append(this.elAmmoType, this.elAmmo)
    right.append(ammoBlock)

    bar.append(left, this.elArmorWrap, center, right)
    this.root.appendChild(bar)

    // --- Bal felső: kulcsok és haladás ---
    const topLeft = el('div', 'topleft')
    this.elKeys = el('div', 'keys')
    const progress = el('div', 'progress')
    this.elEnemies = el('span', 'progress-item')
    this.elSecrets = el('span', 'progress-item')
    progress.append(this.elEnemies, this.elSecrets)
    topLeft.append(this.elKeys, progress)
    this.root.appendChild(topLeft)

    // --- Alacsony élet vörös keret ---
    this.elLowHealth = el('div', 'lowhealth')
    this.root.appendChild(this.elLowHealth)

    parent.appendChild(this.root)
  }

  setVisible(v: boolean): void {
    this.root.style.display = v ? '' : 'none'
  }

  message(text: string): void {
    // Ugyanaz az üzenet közvetlenül egymás után ne halmozódjon.
    const top = this.messages[this.messages.length - 1]
    if (top && top.el.textContent === text) {
      top.ttl = 4
      return
    }
    const item = el('div', 'message')
    item.textContent = text
    this.elMessages.appendChild(item)
    this.messages.push({ el: item, ttl: 4 })
    if (this.messages.length > 5) {
      const old = this.messages.shift()
      old?.el.remove()
    }
  }

  hitMarker(lethal: boolean): void {
    this.hitMarkerT = lethal ? 0.28 : 0.16
    this.elHitMarker.classList.toggle('lethal', lethal)
  }

  update(state: HudState, dtReal: number): void {
    const L = this.last

    // A regeneráció alatt az élet-szám finoman lélegzik. Enélkül a
    // játékos nem tudja, hogy elindult-e az óra, vagy még sebződik.
    if (state.regenActive !== this.regenActive) {
      this.regenActive = state.regenActive
      this.elHealth.classList.toggle('regen', state.regenActive)
    }

    if (state.health !== L.health) {
      L.health = state.health
      this.elHealth.textContent = String(Math.max(0, Math.round(state.health)))
      const frac = state.health / Math.max(1, state.maxHealth)
      this.elHealth.style.color = frac < 0.25 ? '#ff3b1f' : frac < 0.5 ? '#e4b02c' : '#c5556a'
      this.elLowHealth.style.opacity = frac < 0.3 ? String((0.3 - frac) / 0.3) : '0'
    }

    if (state.armor !== L.armor) {
      L.armor = state.armor
      this.elArmor.textContent = String(Math.round(state.armor))
      this.elArmorWrap.style.opacity = state.armor > 0 ? '1' : '0.25'
    }

    const ammoVal = state.ammo ?? -1
    if (ammoVal !== L.ammo || state.ammoType !== L.ammoType) {
      L.ammo = ammoVal
      L.ammoType = state.ammoType
      this.elAmmo.textContent = state.ammo === null ? '∞' : String(state.ammo)
      this.elAmmoType.textContent = AMMO_LABELS[state.ammoType] ?? state.ammoType.toUpperCase()
      // Kevés lőszer: figyelmeztető szín, mielőtt kiürülne.
      const low = state.ammoMax !== null && state.ammo !== null && state.ammo <= Math.max(4, state.ammoMax * 0.12)
      this.elAmmo.style.color = state.ammo === 0 ? '#ff3b1f' : low ? '#e4b02c' : '#64c4cf'
    }

    if (state.weaponId !== L.weapon) {
      L.weapon = state.weaponId
      this.elWeapon.textContent = WEAPON_LABELS[state.weaponId] ?? state.weaponId.toUpperCase()
    }

    const slotKey = state.weapons.map((w) => `${w.slot}${w.owned ? 1 : 0}${w.hasAmmo ? 1 : 0}`).join() + state.weaponId
    if (slotKey !== L.slots) {
      L.slots = slotKey
      this.renderSlots(state)
    }

    const keyKey = state.keys.join()
    if (keyKey !== L.keys) {
      L.keys = keyKey
      this.elKeys.replaceChildren()
      for (const k of state.keys) {
        const key = el('span', 'key')
        key.style.setProperty('--key-color', KEY_COLORS[k])
        this.elKeys.appendChild(key)
      }
    }

    if (state.enemiesLeft !== L.enemies) {
      L.enemies = state.enemiesLeft
      this.elEnemies.textContent = `Ellenfél ${state.enemiesTotal - state.enemiesLeft} / ${state.enemiesTotal}`
    }

    if (state.secretsFound !== L.secrets) {
      L.secrets = state.secretsFound
      this.elSecrets.textContent = `Titok ${state.secretsFound} / ${state.secretsTotal}`
      this.elSecrets.style.display = state.secretsTotal > 0 ? '' : 'none'
    }

    // --- Időzített elemek ---
    if (this.hitMarkerT > 0) {
      this.hitMarkerT -= dtReal
      this.elHitMarker.style.opacity = String(Math.min(1, this.hitMarkerT * 6))
      this.elHitMarker.style.transform = `translate(-50%, -50%) scale(${1.5 - Math.min(1, this.hitMarkerT * 4) * 0.5})`
    } else if (this.elHitMarker.style.opacity !== '0') {
      this.elHitMarker.style.opacity = '0'
    }

    for (let i = this.messages.length - 1; i >= 0; i--) {
      const msg = this.messages[i]
      msg.ttl -= dtReal
      if (msg.ttl <= 0) {
        msg.el.remove()
        this.messages.splice(i, 1)
      } else if (msg.ttl < 0.8) {
        msg.el.style.opacity = String(msg.ttl / 0.8)
      }
    }
  }

  private renderSlots(state: HudState): void {
    this.elSlots.replaceChildren()
    this.slotEls.clear()
    for (const w of state.weapons) {
      const s = el('span', 'slot')
      s.textContent = String(w.slot)
      if (!w.owned) s.classList.add('locked')
      if (w.id === state.weaponId) s.classList.add('active')
      if (w.owned && !w.hasAmmo) s.classList.add('empty')
      this.elSlots.appendChild(s)
      this.slotEls.set(w.slot, s)
    }
  }
}

// ---------------------------------------------------------------------------

function el(tag: string, className: string): HTMLElement {
  const e = document.createElement(tag)
  e.className = className
  return e
}

function labelled(label: string, value: HTMLElement): HTMLElement {
  const block = el('div', 'stat-block')
  const l = el('span', 'stat-label')
  l.textContent = label
  block.append(l, value)
  return block
}
